import { Component, EventEmitter, Input, Output, OnInit, Optional} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip'; 
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TagModule } from 'primeng/tag';


import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

// Interface for an individual email entry
export interface Email 
{
    id: string; // Unique ID for tracking
    email: string;
    EmailRecID?: number; // Optional, indicates if it's an existing record
    EmailCustomerSupplierRecID? : number;
    description: string;
    isActive: number;
    isApproved: number;
}

@Component({
    selector: 'app-email',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        CardModule,
        ToastModule,
        TableModule,
        TooltipModule,
        TagModule
    ],
    templateUrl: './emails.component.html',
    styleUrls: ['./emails.component.scss'],
    providers: [MessageService]
})
export class EmailsComponent implements OnInit {
    @Input()  emails: Email[] = [];
    @Output() emailsChange = new EventEmitter<Email[]>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

	@Output() skipToLastStep = new EventEmitter<void>();

    @Input() customerRecId!: number;
 
    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;
    newEntry: Email = { id: '', email: '', description: '', isActive: 1 , isApproved: 1};

    constructor(private messageService: MessageService,
        public translateServ :TranslateService,
        public dirServ : DirectionService,
        private viewScopeService: ViewScopeService,
        @Optional() public ref: DynamicDialogRef,
        @Optional() public config: DynamicDialogConfig,
        @Optional() private maintainAccountService: MaintainAccountService) {}

    ngOnInit(): void 
    {
        if (this.config && this.config.data)
        {
            console.log("CASE A");
            
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry) 
            {   
                console.log("CASE 1A");

                // Edit mode: initialize with existing record(s)
                this.emails = [this.config.data.nameEntry];
            } 
            else 
            {
                console.log("CASE 2A");

                // Create mode: do NOT pre-push a row here.
                // The footer row with newEntry will handle input.
                this.emails = [];
            }
        }
        else
        {
            console.log("CASE B");
            this.isDialogMode = false;
            this.isCreate = true;
            
            // Ensure all existing items have a unique ID for tracking.
            this.emails.forEach(item => {
                if (!item.id) {
                    item.id = this.generateUniqueId();
                }
            });
            this.resetNewEntry();
        }

        console.warn("Start working with: " + this.customerRecId);
        console.log("Emails check>> isDialog: " + this.isDialogMode + " isCreate: " + this.isCreate)

    }

    private generateUniqueId(): string {
        return 'id_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void {
        this.newEntry = { 
            id: this.generateUniqueId(), 
            email: '', 
            description: '', 
            isActive: 1,      // default active
            isApproved: 1     // default approved
        };
    }

    addEntry(): void {
        if (!this.newEntry.email.trim()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Email for the new entry is required.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        this.emails.push({ ...this.newEntry });
        this.emitChanges();
        
        if(this.isCreate)
        {
            this.resetNewEntry();    
        }

        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New email added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: Email): void {
        this.emails = this.emails.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Additional email removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.emailsChange.emit(this.emails);
    }

    getRemainingChars(description: string): number {
        return this.DESCRIPTION_MAX_LENGTH - description.length;
    }

    validateAndProceed(): void {
        let isValid = true;
        for (const entry of this.emails) {
            if (!entry.email.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Name for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.email}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    toggleIsActive(entry: any): void 
    {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void 
    {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }

    applyRowChanges(entry: Email): void 
    {
        // Simply emit the changes so parent knows data is dirty
        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${entry.email}". Remember to press Save to persist.`,
            life: 2500
        });
    }

    /**
     * This function is intended for the dialog's "save" button.
     * It validates the form and then calls the API to save/update the data.
     * It uses forkJoin to wait for all API calls to complete before closing the dialog.
     */
    saveAndSubmit(): void {
        let isValid = true;

        for (const entry of this.emails) {
            if (!entry.email.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Email for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if ((entry.description || '').length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.email}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        if (this.maintainAccountService) {
            const requests = this.emails.map(entry => {
                const basePayload: any = {
                    Email: entry.email,
                    Descr: entry.description || '',
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
                };

                if (this.isCreate) {
                    basePayload.CustomerSupplierRecID = this.customerRecId;
                } else {
                    basePayload.EmailCustomerSupplierRecID = entry.id;
                    basePayload.EmailRecID = entry.EmailRecID;
                }

                return basePayload;
            });

            console.log("Data we will save:", requests);

            requests.forEach(record => {
                this.maintainAccountService
                    .insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record))
                    .subscribe({
                        next: (response) => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Saved record for "${record.Email}".`,
                                life: 2000
                            });

                            if (this.ref && record === requests[requests.length - 1]) {
                                this.ref.close(response);
                            }
                        },
                        error: () => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: `Failed to save record for "${record.Email}".`,
                                life: 3000
                            });
                        },
                        complete: () => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Complete',
                                detail: 'All records processed.',
                                life: 2000
                            });
                            if (this.ref) {
                                this.ref.close(true);
                            }
                        }
                    });
            });
        }
    }



    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }
}
